import placesAPI from "../../api/placesAPI";
import userAPI from "../../api/userAPI";
import { updateClientAddress } from "../../store/userSlice";
import { distanceInMeters } from "./geo";

// A fix this close to the location already on file is the same place, so there is
// nothing to look up. Without the guard every app open would spend a billed reverse
// geocode and a backend write to re-learn what it already knows — and GPS jitter
// could silently replace an address the user just picked by hand.
const SAME_PLACE_METERS = 100;

// What updateClientLocation sends when the street is missing (see userAPI). The
// Login response echoes it back on later visits, so display code must read it as
// "no street" — formatAddress below does.
const NO_STREET_PLACEHOLDER = "-";

/**
 * The user's address as a single display string.
 *
 * A street of "-" is the backend placeholder for "not provided" (round-tripped
 * from UpdateClientLocation via the Login response) and counts as no street; with
 * no street the house number is meaningless too, so the string is the city alone.
 *
 * Returns "" when every part is missing, so callers can compose it with `||` and fall
 * through to their own placeholder.
 */
export const formatAddress = (street, houseNumber, city) => {
  const realStreet = String(street ?? "").trim();
  if (!realStreet || realStreet === NO_STREET_PLACEHOLDER) return city ?? "";
  return [`${realStreet} ${houseNumber ?? ""}`.trim(), city].filter(Boolean).join(", ");
};

/**
 * Applies a location to the app — the store, and localStorage through the
 * updateClientAddress reducer, which owns persistence. No backend.
 *
 * @param {Object} params
 * @param {{street?: string, houseNumber?: string, city?: string, latitude: number, longitude: number}} params.address
 * @param {"gps"|"address"} params.source - "gps" for a browser fix, "address" for one the user picked
 * @param {Function} params.dispatch
 * @returns {Object} the stored location, including its `formatted` string
 */
export const applyClientAddress = ({ address, source, dispatch }) => {
  const stored = {
    street: address.street,
    houseNumber: address.houseNumber,
    city: address.city,
    latitude: address.latitude,
    longitude: address.longitude,
    formatted: formatAddress(address.street, address.houseNumber, address.city),
    source,
  };

  dispatch(updateClientAddress(stored));

  return stored;
};

/**
 * THE single pairing of the UpdateClientLocation backend write with the local
 * apply — every location change in the app goes through here, so the store,
 * localStorage and the backend can never learn about a change separately.
 *
 * `optimistic` decides the order:
 *   - false (the address screen): backend first, then apply. The screen must not
 *     claim an address the DB refused — a failure throws {code, mess} and nothing
 *     is stored; the caller decides whether that deserves a dialog.
 *   - true (background GPS enrichment): apply first, backend after. The pill can
 *     show the address the moment the words exist, and the UpdateClientLocation
 *     write queues behind the carousel calls — making the screen wait on it was
 *     pure delay. If the write then fails, the UI and localStorage keep the
 *     address while the DB doesn't: the caller logs it, and it is still better
 *     than showing nothing at all.
 *
 * @param {Object} params
 * @param {{street?: string, houseNumber?: string, city?: string, latitude: number, longitude: number}} params.address
 * @param {"gps"|"address"} params.source
 * @param {Object} params.mainUserData - for the credentials
 * @param {Function} params.dispatch
 * @param {boolean} [params.optimistic=false]
 * @returns {Promise<Object>} the stored location, including its `formatted` string
 */
export const commitClientLocation = async ({ address, source, mainUserData, dispatch, optimistic = false }) => {
  const stored = optimistic ? applyClientAddress({ address, source, dispatch }) : null;

  await userAPI.updateClientLocation({
    token: mainUserData.Token,
    id: mainUserData.ID,
    phone: mainUserData.Phone,
    latitude: address.latitude,
    longitude: address.longitude,
    houseNumber: address.houseNumber,
    city: address.city,
    street: address.street,
  });

  return stored ?? applyClientAddress({ address, source, dispatch });
};

/**
 * Commits a browser GPS fix as the user's location: coordinates land in the store
 * synchronously (so the carousels re-rank before any network round trip), then the
 * fix is reverse-geocoded into words and committed to the backend in the
 * background.
 *
 * Call it fire-and-forget — it never throws and never opens a dialog. A missing
 * Geocoding API key or a failed write leaves the coordinates applied and the
 * previous label in place.
 *
 * @param {Object} params
 * @param {{latitude: number, longitude: number}} params.coords - the fresh fix
 * @param {Object|null} params.clientAddress - the location already stored, if any
 * @param {Object} params.mainUserData - for the credentials
 * @param {Function} params.dispatch
 */
export async function commitGpsFix({ coords, clientAddress, mainUserData, dispatch }) {
  if (!coords || !mainUserData?.Token) return;

  // The guard must run before the dispatch below — after it, the stored location
  // IS this fix and the distance would always be zero.
  const movedBy = distanceInMeters(coords, {
    latitude: clientAddress?.latitude,
    longitude: clientAddress?.longitude,
  });
  // null = nothing on file yet, so there is nothing to compare and we do want it.
  if (movedBy !== null && movedBy < SAME_PLACE_METERS) return;

  // Coordinates first, words later: dispatched before the first await, so a caller
  // that navigates to home right after this call already ranks by the fresh fix,
  // even if the geocode below is slow or fails. formatted is "" — a fix has no
  // words, and the pill falls through to the profile address meanwhile.
  applyClientAddress({
    address: { latitude: coords.latitude, longitude: coords.longitude },
    source: "gps",
    dispatch,
  });

  try {
    const geocoded = await placesAPI.reverseGeocode(coords);
    // A fix in the middle of nowhere can come back with no street and no city; saving
    // that would replace a real address with an empty label.
    if (!geocoded.street && !geocoded.city) return;

    const address = { ...geocoded, latitude: coords.latitude, longitude: coords.longitude };

    await commitClientLocation({ address, source: "gps", mainUserData, dispatch, optimistic: true });
  } catch (err) {
    console.error("Could not resolve an address for the current location:", err);
  }
}
